import { Box, Divider, Paper, Stack, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import moment from 'moment'
import { CrpEmissionsTable } from './crp-emissions-table'
import { CrpPlanType, ICrpFigures } from '../../../../shared/services/crp-report/crp-report.model'

/**
 * The plan fields the rendered document needs. The public share endpoint returns exactly this shape;
 * the signed-in detail page assembles it from the plan record and its frozen figures.
 */
export interface ICrpPlanDocumentData {
  organisationName: string
  /** The organisation's uploaded logo; the document renders without one when absent. */
  organisationLogoUrl?: string | null
  planType: CrpPlanType
  publicationDate: string | null
  generatedAt: string | null
  commitmentText: string | null
  targetsNarrative: string | null
  completedMeasuresNarrative: string | null
  plannedMeasuresNarrative: string | null
  baselineAdditionalDetails: string | null
  currentAdditionalDetails: string | null
  progressNarrative: string | null
  signOffName: string | null
  signOffTitle: string | null
  signOffDate: string | null
  figures: ICrpFigures
  declarationParagraphs: string[]
  disclaimerText: string
}

/**
 * The Carbon Reduction Plan rendered as the Annex A document. Shared by the public share page and the
 * signed-in plan view so the two can never drift apart. Renders frozen figures only; it has no
 * awareness of live data or of who is looking at it.
 */
export const CrpPlanDocument: FunctionComponent<{ plan: ICrpPlanDocumentData }> = ({ plan }) => {
  const isProgress = plan.planType === 'PROGRESS'
  const { baseline, current } = plan.figures.periods

  const narrative = (text: string | null) => (
    text
      ? <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{text}</Typography>
      : null
  )

  const heading = (text: string) => (
    <>
      <Typography variant='h6' sx={{ mt: 4, mb: 1 }}>{text}</Typography>
      <Divider sx={{ mb: 2, borderBottomWidth: 2, borderColor: 'primary.main' }} />
    </>
  )

  return (
    <Paper variant='outlined' sx={{ p: { xs: 3, md: 5 } }}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
        <Box>
          <Typography variant='h4' sx={{ mb: 2 }}>
            {isProgress ? 'Carbon Reduction Progress Report' : 'Carbon Reduction Plan'}
          </Typography>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>Supplier name:</strong> {plan.organisationName}
          </Typography>
          <Typography variant='body2' sx={{ mb: 2 }}>
            <strong>Publication date:</strong>{' '}
            {plan.publicationDate ? moment(plan.publicationDate).format('D MMMM YYYY') : '—'}
          </Typography>
        </Box>
        {/* Fixed-height box so the header does not reflow as the image loads. */}
        {plan.organisationLogoUrl && (
          <Box sx={{ height: 56, flexShrink: 0 }}>
            <img
              src={plan.organisationLogoUrl}
              alt={`${plan.organisationName} logo`}
              style={{ maxHeight: 56, maxWidth: 180, objectFit: 'contain' }}
            />
          </Box>
        )}
      </Stack>

      {!isProgress && (
        <>
          {heading('Commitment to achieving Net Zero')}
          {narrative(plan.commitmentText)}

          {heading('Baseline Emissions Footprint')}
          <Typography variant='body2' sx={{ mb: 1 }}>
            <strong>Baseline Year:</strong> {baseline?.label || '—'}
          </Typography>
          {plan.figures.baselineEnteredManually && (
            <Typography variant='body2' sx={{ mb: 1 }}>
              {(baseline?.scope3.length ?? 0) > 0
                ? 'The baseline figures were entered manually when the base year was recorded, including its Scope 3 by category. Any Scope 3 not broken down by category is shown under other categories.'
                : 'The baseline figures were entered manually when the base year was recorded. Its Scope 3 was entered as a single total, so it is not broken down by category.'}
            </Typography>
          )}
          {narrative(plan.baselineAdditionalDetails)}
          <CrpEmissionsTable period={baseline} />

          {heading('Current Emissions Reporting')}
          <Typography variant='body2' sx={{ mb: 1 }}>
            <strong>Reporting Year:</strong> {current?.label || '—'}
          </Typography>
          <CrpEmissionsTable period={current} />
          {narrative(plan.currentAdditionalDetails)}
        </>
      )}

      {heading('Emissions reduction targets')}
      {narrative(plan.targetsNarrative)}

      {heading('Carbon Reduction Projects')}
      <Typography variant='subtitle2' sx={{ mb: 1 }}>Completed Carbon Reduction Initiatives</Typography>
      {narrative(plan.completedMeasuresNarrative)}
      {narrative(plan.plannedMeasuresNarrative)}

      {isProgress && plan.progressNarrative && (
        <>
          {heading('Progress commentary')}
          {narrative(plan.progressNarrative)}
        </>
      )}

      {heading('Methodology')}
      <Typography variant='body2' sx={{ mb: 2 }}>{plan.figures.methodologyText}</Typography>

      {heading('Declaration and Sign Off')}
      {plan.declarationParagraphs.map((paragraph, index) => (
        <Typography variant='body2' sx={{ mb: 1.5 }} key={index}>{paragraph}</Typography>
      ))}
      <Typography variant='body2' sx={{ mt: 3 }}>
        <strong>Signed on behalf of the Supplier:</strong>
      </Typography>
      <Typography variant='body2'>
        {plan.signOffName || ''}{plan.signOffTitle ? `, ${plan.signOffTitle}` : ''}
      </Typography>
      <Typography variant='body2' sx={{ mt: 1 }}>
        <strong>Date:</strong>{' '}
        {plan.signOffDate ? moment(plan.signOffDate).format('D MMMM YYYY') : '—'}
      </Typography>

      <Divider sx={{ my: 4 }} />
      <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
        {plan.disclaimerText}
      </Typography>
    </Paper>
  )
}
